/**
 * Partners Index — Route Module
 *
 * SEO thin-content pass, and a correction. `/partners` carried two paragraphs
 * (SEO F16) that described a business this platform does not run: they said a
 * partner is an agency listing homes here, that a partner page shows "every
 * home they currently have available", and that enquiries reach the agency
 * directly. NONE OF THAT IS TRUE OF THE DATA. `partner_id` exists on exactly
 * one table — `posts` — and `PartnerDetail` carries a name, a role, a website
 * and the partner's POSTS. A partner is a Hong Kong home-service business that
 * writes guides here; there is no partner→listing relation anywhere in the
 * schema, and `PartnersService` calls itself "Content Partners/Authors".
 *
 * Confidently wrong copy is worse than a thin page, so the fix is not more
 * words — it is true words, and where possible words the page can prove:
 *
 *   * how many partners there are — `paginated.meta.totalCount`, the pager's
 *     own count, from the response that renders the grid;
 *   * what they cover — the distinct `role` strings of the partners on this
 *     page, already localized by the backend, already printed on every card.
 *
 * ⚠ Measured in CHARACTERS PER LOCALE, never words. Ahrefs' "low word count"
 * flag whitespace-tokenizes, so it scores Chinese at roughly a quarter and
 * flagged no `/en/` page on this site at all.
 */
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { PaginatedDirectory } from '@letsgethome/ui/paginated-directory';
import { useLoaderData } from 'react-router';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { Link } from '../../../../shared/i18n/routing';
import { useLocale, useTranslations } from '../../../../shared/i18n/useTranslations';
import { PartnerCard } from '../components/PartnerCard';
import type { loader } from './partners.loader';

export function Component() {
  const { paginated, roles, seo } = useLoaderData<typeof loader>();
  const t = useTranslations('partners');
  const locale = useLocale();

  // "A, B and C" in en; "A、B、C" in zh — the conjunction is the runtime's job,
  // not a hand-written separator per locale.
  const listFormat = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
  const roleList = listFormat.format(roles);

  return (
    <>
      <SeoHead {...seo} />
      <div className="min-h-screen bg-muted">
        <div className="bg-gradient-to-b from-primary to-primary/90 py-16">
          <PageContainer size="7xl" className="sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-white">{t('title')}</h1>
            <p className="mt-2 text-lg text-primary-foreground/80">{t('subtitle')}</p>
          </PageContainer>
        </div>

        <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
          {/* Derived lede — see the module header. Suppressed when the
              directory is empty: `paginate` already renders the empty state
              below, and "0 partner businesses" over it is noise, not content. */}
          {paginated.meta.totalCount > 0 && (
            <div className="mb-10 max-w-3xl space-y-3 leading-relaxed text-muted-foreground">
              <p>{t('directory.count', { count: paginated.meta.totalCount })}</p>
              {roles.length > 0 && (
                <p>{t('directory.roles', { roles: plaintextString(roleList) })}</p>
              )}
            </div>
          )}

          <PaginatedDirectory
            data={paginated}
            itemKey={(partner) => partner.id}
            renderItem={(partner) => <PartnerCard partner={partner} />}
            hrefForPage={(n) => routes.partners.index({ page: n > 1 ? n : undefined })}
            Link={Link}
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            empty={
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t('noPartners')}</p>
              </div>
            }
          />

          {/* SEO F16: the page was a heading plus a card grid — thin for a
              crawler and unhelpful to anyone who does not already know what
              a "partner" is here. These sections say what the relationship
              is, what a partner page holds, and how to enter it. */}
          <section className="mt-16 grid max-w-5xl grid-cols-1 gap-10 md:grid-cols-2">
            <article>
              <h2 className="text-xl font-semibold">{t('about.title')}</h2>
              <p className="mt-3 leading-relaxed text-muted-foreground">{t('about.body')}</p>
            </article>
            <article>
              <h2 className="text-xl font-semibold">{t('directory.how.title')}</h2>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {t('directory.how.body')}
              </p>
            </article>
            <article>
              <h2 className="text-xl font-semibold">{t('howToJoin.title')}</h2>
              <p className="mt-3 leading-relaxed text-muted-foreground">{t('howToJoin.body')}</p>
              <Link
                href={routes.contact()}
                prefetch="intent"
                className="mt-4 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                {t('howToJoin.cta')}
              </Link>
            </article>
            <article>
              <h2 className="text-xl font-semibold">{t('readGuides.title')}</h2>
              <p className="mt-3 leading-relaxed text-muted-foreground">{t('readGuides.body')}</p>
              <Link
                href={routes.blog.index()}
                prefetch="intent"
                className="mt-4 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                {t('readGuides.cta')}
              </Link>
            </article>
          </section>
        </PageContainer>
      </div>
    </>
  );
}
